import { useCallback, useEffect, useMemo, useState } from 'react';
import { Linking, Platform } from 'react-native';
import InAppBrowser from 'utils/packages/inAppBrowser';
import Sentry from 'utils/sentry';
import { openSmsComposer } from 'utils/packages/rnSms';
import { noop } from 'lodash';
import { useFetch, useEventBusDispatch, useInterval, useFetchOld, muteErrorAndResultButThrows, ThrowableFetchError, } from 'hooks';
import { getSyncingStatus, syncSingleAccountConnection } from 'actions';
import Amplitude from 'utils/amplitude';
import { DeepLinkType, parseDeepLink, } from 'common/NavigatorActions/hooks/parseDeepLink';
import { useAppDispatch } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import Modal from 'utils/packages/Modal';
import { fulfillPaymentRequest, getPaymentInfo, sendInternalPayment, sendPayment, sendPaymentToBeneficiary, } from '../actions';
import { mapToPaymentActivity } from '../types/types';
import { busEvents } from '../constants';
const getApiCall = (props) => {
    const { bankAccount, amount, reference } = props;
    const toBankAccount = 'toBankAccount' in props ? props.toBankAccount : undefined;
    const toBeneficiaryId = 'toBeneficiaryId' in props ? props.toBeneficiaryId : undefined;
    if ('token' in props && props.token) {
        return fulfillPaymentRequest(props.token, bankAccount.id, props.bankAccount.bankId, amount, reference);
    }
    if ('userHandle' in props && props.userHandle) {
        return sendPayment(bankAccount.id, props.bankAccount.bankId, props.userHandle, amount, reference, props.payeeAccountId, props.onPaymentMadeCallback);
    }
    if (toBankAccount) {
        return sendInternalPayment(bankAccount.id, toBankAccount.id, amount, reference);
    }
    if (toBeneficiaryId) {
        return sendPaymentToBeneficiary(bankAccount.id, toBeneficiaryId, amount, reference);
    }
    return null;
};
export var PollingStage;
(function (PollingStage) {
    PollingStage[PollingStage["FIVE_SECONDS"] = 5] = "FIVE_SECONDS";
    PollingStage[PollingStage["TEN_SECONDS"] = 10] = "TEN_SECONDS";
    PollingStage[PollingStage["FIFTEEN_SECONDS"] = 15] = "FIFTEEN_SECONDS";
})(PollingStage || (PollingStage = {}));
export const useCreatePayment = (props, callbacks) => {
    const logParams = useMemo(() => props
        ? {
            bankAccountId: props.bankAccount.id,
            bankId: 'bankId' in props.bankAccount
                ? props.bankAccount.bankId
                : undefined,
            userHandle: 'userHandle' in props ? props.userHandle : undefined,
            token: 'token' in props ? props.token : undefined,
            amount: props.amount,
            isRequest: 'token' in props && props.token !== undefined,
            type: props.type,
        }
        : {}, [props]);
    const [, , , createPayment] = useFetchOld(muteErrorAndResultButThrows);
    const [fetchPaymentInfo, paymentInfoBusy] = useFetch();
    const [state, setState] = useState('creating');
    const [result, setResult] = useState();
    const [polling, setPolling] = useState(false);
    const broadcastEvent = useEventBusDispatch();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const [pollingStage, setPollingStage] = useState();
    useEffect(() => {
        let fiveSec;
        let tenSec;
        let fifteenSec;
        if (polling) {
            fiveSec = setTimeout(() => {
                setPollingStage(PollingStage.FIVE_SECONDS);
                tenSec = setTimeout(() => {
                    setPollingStage(PollingStage.TEN_SECONDS);
                    fifteenSec = setTimeout(() => {
                        setPollingStage(PollingStage.FIFTEEN_SECONDS);
                    }, 5000);
                }, 5000);
            }, 5000);
        }
        return () => {
            clearTimeout(fiveSec);
            clearTimeout(tenSec);
            clearTimeout(fifteenSec);
        };
    }, [polling]);
    useEffect(() => {
        if (!props) {
            callbacks?.onBack();
            return () => { };
        }
        const apicall = getApiCall(props);
        if (!apicall) {
            callbacks?.onBack();
            return () => { };
        }
        Amplitude.logEvent('Create Direct Payment', logParams);
        createPayment(apicall)
            .then((result) => {
            if (result) {
                setResult(result);
                Linking.openURL(result.link);
            }
        })
            .catch((error) => {
            if (error instanceof ThrowableFetchError) {
                Amplitude.logEvent('Completed Direct Payment', {
                    ...logParams,
                    status: 'failed',
                    reason: error.fetchError?.apiPayload?.statusText ??
                        error.fetchError?.apiPayload?.status,
                });
                if ((('userHandle' in props && props.userHandle) ||
                    ('token' in props && props.token)) &&
                    error.fetchError?.apiPayload?.status === 409) {
                    setState('payeeNoDefAccount');
                }
                else {
                    Modal.showError(error.message);
                    callbacks?.onBack();
                }
            }
        });
        const handleOpenURL = (event) => {
            if (!event.url ||
                event.url.includes('onelink.emma-app.com') ||
                event.url.includes('emma.onelink.me') ||
                event.url.includes('awin.onelink.me') ||
                event.url.includes('emmapublisher.onelink.me')) {
                return;
            }
            const deepLinkData = parseDeepLink(event.url);
            switch (deepLinkData.type) {
                case DeepLinkType.PAYMENT_SUCCESS:
                    if ('userHandle' in props && props.userHandle) {
                        broadcastEvent(busEvents.PAYMENT_SENT);
                    }
                    else if ('token' in props && props.token) {
                        broadcastEvent(busEvents.REQUEST_FULFILLED);
                    }
                    setPolling(true);
                    setState('pending');
                    break;
                case DeepLinkType.PAYMENT_FAIL:
                    setState('bankFail');
                    break;
                case DeepLinkType.PAYMENT_ABORT:
                    setState('bankAbort');
                    Amplitude.logEvent('Completed Direct Payment', {
                        ...logParams,
                        status: 'failed',
                        reason: event.url,
                    });
                    break;
                default:
                    Amplitude.logEvent('Completed Direct Payment', {
                        ...logParams,
                        status: 'failed',
                        reason: event.url,
                    });
                    callbacks?.onBack();
                    break;
            }
            if (Platform.OS === 'ios' && !event.url.includes('/plaid')) {
                InAppBrowser.close();
            }
        };
        const linkingSubsription = Linking.addEventListener('url', handleOpenURL);
        return () => {
            linkingSubsription.remove();
            setPolling(false);
        };
        // one-time effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // 500ms > 1000ms after 5s > 2000ms after 10s
    const interval = useMemo(() => {
        if (!polling)
            return undefined;
        if (!pollingStage) {
            return 500;
        }
        if (pollingStage === PollingStage.FIVE_SECONDS) {
            return 1000;
        }
        return 2000;
    }, [polling, pollingStage]);
    useInterval(() => {
        if (!paymentInfoBusy && result?.paymentId) {
            fetchPaymentInfo(getPaymentInfo(result.paymentId))
                .then(async (result) => {
                if (props && result.status !== 'PENDING') {
                    const { bankAccount, options } = props;
                    const toBankAccount = 'toBankAccount' in props ? props.toBankAccount : undefined;
                    Amplitude.logEvent('Completed Direct Payment', {
                        ...logParams,
                        status: 'success',
                    });
                    setPolling(false);
                    const payment = mapToPaymentActivity(result, options);
                    if (payment) {
                        callbacks?.onFinish();
                        navigation.navigateDeprecated('PaymentActivity', { payment });
                    }
                    const promises = [
                        dispatch(syncSingleAccountConnection(bankAccount.id, true)),
                    ];
                    if (toBankAccount?.bankConnectionId) {
                        promises.push(dispatch(syncSingleAccountConnection(toBankAccount.id, true)));
                    }
                    await Promise.all(promises).catch(noop);
                    dispatch(getSyncingStatus());
                }
            })
                .catch(noop);
        }
    }, interval);
    const retryBank = useCallback(() => {
        if (result?.link) {
            Linking.openURL(result.link);
        }
    }, [result?.link]);
    const sendSms = useCallback((phoneNumbers = []) => {
        openSmsComposer({
            body: 'Can you set up your Pay tab in Emma? I need it to send you money.',
            recipients: phoneNumbers,
        })
            // The reminder is handed over to the SMS app, so the payment flow is
            // done. Whether the message is then sent can't be known.
            .then(() => callbacks?.onFinish())
            .catch((error) => {
            Sentry.logError({
                filename: 'createPmt.ts',
                message: 'Open SMS failed',
                err: error,
            });
        });
    }, [callbacks]);
    return [state, retryBank, sendSms, pollingStage];
};
